<template>
  <v-card class="mb-4 elevation-0 rounded-lg" border>
    <v-card-text>
      <v-row align="start" class="mb-2" v-if="!isLoadingResults">
        <v-col cols="8" class="text-start">
          <v-row align="start" v-if="!isLoadingResults">
            <v-col cols="2">
              <img v-if="scan.scanStatus === -1" :src="asset('icon/saved.png')" alt="Saved on-device and uploaded without validation." />
              <img v-if="scan.scanStatus === 0" :src="asset('icon/invalid.png')" alt="Invalid / duplicate scan." />
              <img v-if="scan.scanStatus === 1" :src="asset('icon/valid.png')" alt="Recorded / valid scan." />
            </v-col>
            <v-col cols="10">
              <h3 v-if="scan.scanValue.length > 0" class="text-black">
                {{ scan['scanValue'].length > 100 ? scan['scanValue'].substr(0, 99) + '...' : scan['scanValue'] }}
                <v-tooltip activator="parent" location="bottom" max-width="400">
                  {{ scan['scanValue'] ? scan['scanValue'] : ''}}
                </v-tooltip>
              </h3>
              <div v-if="scan.resultType === 'curl'">
                <v-btn variant="text" :href="scan.scanResponse" target="_blank">See Response <v-icon small>open_in_new</v-icon></v-btn>
              </div>
              <div v-if="scan.resultType === 'html'">
                <v-btn variant="text" :to="{ name: 'app.scanresponse', params:{scanId: scan.scanId}}" >See HTML Response <v-icon small>open_in_new</v-icon></v-btn>
                <v-tooltip activator="parent" location="bottom" max-width="400">
                  {{ decodeURI(scan.scanResponse) }}
                </v-tooltip>
              </div>
              <div v-if="scan.resultType == undefined && scan.scanResponse && scan.scanResponse.length > 0">
                <span>{{ scan['scanResponse'].length > 250 ? scan['scanResponse'].substr(0, 249) + '...' : scan['scanResponse'] }}</span>
                <v-tooltip activator="parent" location="bottom" max-width="400">
                  {{ scan['scanResponse'] ? scan['scanResponse'] : '' }}
                </v-tooltip>
              </div>
              <v-chip v-if="isEmergencyScan" color="red" size="small" append-icon="mdi-alert-circle-outline" class="mt-2">
                Emergency scan
              </v-chip>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="4" class="text-right">
          <v-btn variant="text" class="text-none" append-icon="mdi-pencil" v-if="isPreview && scan.service._validationMethod !== 'databasebuilder'" :to="{name: 'account.scans.edit', query: {scanId: scan.scanId}}">Edit</v-btn>
          <v-btn class="text-none" variant="text" @click="deletingScanId = scan.scanId; isDeleteScanModalOpen = true" v-if="!scan.isActive" append-icon="mdi-delete" size="small">Delete</v-btn>
          <span v-else>{{scan.type === 'checkinout' ? 'Check In/Out' : 'Table Builder'}}</span>
        </v-col>
      </v-row>

      <v-expansion-panels v-model="panel" variant="accordion" multiple>
        <v-expansion-panel @click="loadQuestions(scan.scanId)" :class="{ 'bg-indigo-lighten-4 my-1 border': panel.includes(0), 'rounded': true }" elevation="0">
          <v-expansion-panel-title class="font-weight-bold px-0 my-n4 text-body-2" hide-actions>
            <div :class="{ 'bg-indigo-lighten-4 w-100 pa-2 rounded font-weight-bold text-subtitle-1': true, 'border': !panel.includes(0)}">
              <v-icon class="mt-n1" size="large" :icon="panel.includes(0) ? 'mdi-menu-down' : 'mdi-menu-right'" /> Questions
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <h4 v-show="isLoadingQuestions" class="text-center py-4">
              <em class="fa fa-spinner fa-spin"></em> <span>Loading, please wait ...</span>
              <br>
              <br>
            </h4>
            <div class="text-center py-4" v-show="scanQuestions.length == 0 && !isLoadingQuestions">No Questions</div>
            <v-list-item class="py-2" v-if="!isLoadingQuestions" v-for="question in scanQuestions">
              <v-list-item-title><strong>{{ question['questionText'] }}</strong></v-list-item-title>
              <v-list-item-subtitle v-for="answer in question.answer" class="mt-1">
                <a style="font-size: 16px; color: black" :href="'https://maps.google.com?q=' + answer.answerText" target="_blank" v-if="answer.type == 'geo'">
                  {{ answer.answerText }}
                </a>

                <template v-if="(answer.type == 'dropbox' || answer.type == 'json') && answer.link != ''">
                  <a :href="answer.link" target="_blank">
                    <img style="max-width: 180px; margin: auto;display: block" :src="answer.thumbimg" class="maxWDropbox img-thumbnail" @error="imageError(answer);" alt="max dropbox">
                  </a>
                  <br>
                  <div style="font-size: 16px; text-align: center">
                    {{ answer.file }}
                  </div>
                </template>

                <a :href="answer.answerText" target="_blank" v-if="answer.type == 'base64'">
                  <img :src="answer.thumbimg" class="maxWBase64 img-thumbnail" @error="imageError(answer);" alt="max Wbase">
                </a>

                <div style="font-size: 16px;color: black !important" v-if="answer.type == ''" class="font-normal">{{ answer.answerText }}</div>
              </v-list-item-subtitle>
            </v-list-item>
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel @click="loadProperties(scan.scanId)" :class="{ 'bg-indigo-lighten-4 my-1 border': panel.includes(1), 'rounded': true }" elevation="0">
          <v-expansion-panel-title class="font-weight-bold px-0 my-n3 text-body-2" hide-actions>
            <div :class="{ 'bg-indigo-lighten-4 w-100 pa-2 rounded font-weight-bold text-subtitle-1': true, 'border': !panel.includes(1)}">
              <v-icon class="mt-n1" size="large" :icon="panel.includes(1) ? 'mdi-menu-down' : 'mdi-menu-right'" /> Properties
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <h4 v-show="isLoadingProperties" class="text-center">
              <em class="fa fa-spinner fa-spin"></em> <span>Loading, please wait ...</span>
              <br>
              <br>
            </h4>
            <div class="text-center" v-if="scanProperties.length == 0">No Properties</div>
            <v-list-item v-if="!isLoadingProperties" v-for="property in scanProperties">
              <v-list-item-title>
                <strong>{{ property['friendlyName'] != '' ? property['friendlyName'] : property.name }}</strong>: {{ property.type != undefined &&  property.type == 'geo' ? property['modifiedValue']  : property.value }}
                <small v-if="property.type == 'geo'">
                  <a :href="'https://maps.google.com?q=' + property.modifiedValue" target="_blank">
                    <v-icon icon="mdi-link"></v-icon>
                  </a>
                </small>
              </v-list-item-title>
            </v-list-item>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>

    <v-card-actions class="justify-space-around d-block border bg-grey-lighten-3 rounded mx-4 mt-n3 mb-2">
      <div class="text-subtitle d-inline-block mx-4 hover-styling" v-if="!receivedTimestamp && !modifiedTimestamp && !bothTimestamp"
             @click="router.push({name: route.name, query: {start_date: getDate(scan.timestamp.scannedTimestamp, 'YYYY-MM-DD'), end_date: getDate(scan.timestamp.scannedTimestamp, 'YYYY-MM-DD')}})">
        <v-icon class="mt-n1" icon="mdi-clock-time-four-outline"/> Scanned @ {{ getDate(scan.timestamp.scannedTimestamp, 'MMM DD, YYYY h:mm A')}}
        <v-tooltip activator="parent" location="bottom">
          Only show scans made on {{ getDate(scan.timestamp.scannedTimestamp, 'MMM DD, YYYY') }}
        </v-tooltip>
      </div>

      <div class="text-subtitle d-inline-block mx-4 hover-styling" v-if="receivedTimestamp && scan.timestamp.receivedTimestamp !== undefined && !bothTimestamp"
             @click="router.push({ name: route.name, query: { start_date: getDate(scan.timestamp.receivedTimestamp, 'YYYY-MM-DD'), end_date: getDate(scan.timestamp.receivedTimestamp, 'YYYY-MM-DD') }})">
        <v-icon class="mt-n1" size="small" icon="mdi-clock-time-four-outline" /> Received @ {{ getDate(scan.timestamp.receivedTimestamp, 'MMM DD, YYYY h:mm A') }}
        <v-tooltip activator="parent" location="bottom">
          Only show scans made on {{ getDate(scan.timestamp.receivedTimestamp, 'MMM DD, YYYY') }}
        </v-tooltip>
      </div>

      <div class="text-subtitle d-inline-block mx-4 hover-styling" v-if="modifiedTimestamp && scan.timestamp.modifiedTimestamp !== undefined && !bothTimestamp"
             @click="router.push({name: route.name, query: {start_date: getDate(scan.timestamp.modifiedTimestamp, 'YYYY-MM-DD'), end_date: getDate(scan.timestamp.modifiedTimestamp, 'YYYY-MM-DD')}})">
        <v-icon size="small" class="mt-n1" icon="mdi-clock-time-four-outline" />Modified @ {{ getDate(scan.timestamp.modifiedTimestamp, 'MMM DD, YYYY h:mm A') }}
        <v-tooltip activator="parent" location="bottom">
          Only show scans made on {{ getDate(scan.timestamp.modifiedTimestamp, 'MMM DD, YYYY') }}
        </v-tooltip>
      </div>

      <div class="text-subtitle d-inline-block mx-4 hover-styling" v-if="bothTimestamp"
             @click="scan.timestamp.modifiedTimestamp ? router.push({name: route.name, query: {start_date: getDate(scan.timestamp.modifiedTimestamp, 'YYYY-MM-DD'), end_date: getDate(scan.timestamp.modifiedTimestamp, 'YYYY-MM-DD')}}) : router.push({name: route.name, query: {start_date: getDate(scan.timestamp.receivedTimestamp, 'YYYY-MM-DD'), end_date: getDate(scan.timestamp.receivedTimestamp, 'YYYY-MM-DD')}})">
        <v-icon class="mt-n1" size="small" icon="mdi-clock-time-four-outline" /> {{scan.timestamp.modifiedTimestamp ? 'Modified' : 'Received' }} @ {{ scan.timestamp.modifiedTimestamp ? getDate(scan.timestamp.modifiedTimestamp, 'MMM DD, YYYY h:mm A') : getDate(scan.timestamp.receivedTimestamp, 'MMM DD, YYYY h:mm A')}}
        <v-tooltip activator="parent" location="bottom">
          Only show scans made on {{ scan.timestamp.modifiedTimestamp ? getDate(scan.timestamp.modifiedTimestamp, 'MMM DD, YYYY') : getDate(scan.timestamp.receivedTimestamp, 'MMM DD, YYYY') }}
        </v-tooltip>
      </div>

      <div class="text-subtitle d-inline-block mx-4 hover-styling" @click="router.push({name: route.name, query: {service_ids: scan.service.serviceId, period: 'all'}})">
        <v-icon class="mt-n1" size="small" icon="mdi-file" /> {{ scan.service.name }}
        <v-tooltip activator="parent" location="bottom">
          Only show scans made with {{ scan.service.name }}
        </v-tooltip>
      </div>

      <div class="text-subtitle d-inline-block mx-4 hover-styling" @click="router.push({name: route.name, query: {device_ids: scan.device.deviceId, period: 'all'}})">
        <v-icon class="mt-n1" size="small" icon="mdi-cellphone" /> {{ scan.device.name }}
        <v-tooltip activator="parent" location="bottom">
          Only show scans made using {{ scan.device.name }}
        </v-tooltip>
      </div>

      <div class="text-subtitle d-inline-block mx-4 hover-styling" @click="router.push({name: route.name, query: {user_ids: scan.createdUser.userId, u_deleted: 0, period: 'all'}})">
        <v-icon size="small" class="mt-n1" icon="mdi-account" /> {{ scan.createdUser.username }}
        <v-tooltip activator="parent" location="bottom">
          Only show scans made by  {{ scan.createdUser.username }}
        </v-tooltip>
      </div>

      <div class="text-subtitle d-inline-block mx-4 hover-styling" @click="router.push({name: route.name, query: {scan_ids: scan.scanId, period: 'all' }})">
        <v-icon class="mt-n1" size="small" icon="mdi-file" /> Scan ID {{ scan.scanId }}
        <v-tooltip activator="parent" location="bottom">
          Only show this {{ scan.scanId }} scan
        </v-tooltip>
      </div>

      <div class="text-subtitle d-inline-block mx-4 hover-styling" @click="router.push({name: 'account.externalaccount.postback', query: { scanId: scan.scanId }})" v-if="isPostbackDone">
        <v-icon class="mt-n1" size="small" icon="mdi-share" /> Postback Log
        <v-tooltip activator="parent" location="bottom"> View Postback Log of this scan</v-tooltip>
      </div>
    </v-card-actions>
  </v-card>

  <v-dialog v-model="isDeleteScanModalOpen" width="500">
    <v-card text="Are you sure you want to delete this scan? You will not be able to undo this action.">
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text="Yes" @click="deleteScan" :disabled="isDisabled" variant="tonal"></v-btn>
        <v-btn text="Cancel" @click="isDeleteScanModalOpen = false" :disabled="isDisabled" color="black" variant="elevated"></v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import crApi from "../service/CrApi";
import securityService from "../service/SecurityService";
import flashMessenger from '../service/FlashMessenger';
import asset from '../service/AssetService';
import moment from "moment";

const props = defineProps({
  scan: {
    type: Object,
    required: true
  },
  received: {
    type: [String, Number],
    default: null
  },
  scanFilterStateV2: Object,
});

const isDisabled = ref(false);
const route = useRoute();
const isPreview = ref(false);
const receivedTimestamp = ref(null);
const isLoadingResults = ref(true);
const scanQuestions = ref([]);
const scanProperties = ref([]);
const showQuestions = ref(false);
const showProperties = ref(false);
const isPostbackDone = ref(false);
const isEmergencyScan = ref(false);
const scan = ref(props.scan);
const isLoadingQuestions = ref(false);
const isLoadedQuestions = ref(false);
const isDeleteScanModalOpen = ref(false);
const deletingScanId  = ref(null);
const isLoadingProperties = ref(false);
const isLoadedProperties = ref(false);
const modifiedTimestamp = ref(null);
const bothTimestamp = ref(null);
const currentStates = ref(['account.scans.search', 'account.scans.search.list', 'account.scans.uploads', 'account.scans.bulk_scan_removal']);
const currentState = ref(route.name as string);
const panel = ref([]);
const router = ref(useRouter());

onMounted(() => {
  isPreview.value = route.query.preview !== undefined;
  receivedTimestamp.value = parseInt(props.received as string) == 1;
  modifiedTimestamp.value = parseInt(props.received as string) == 2;
  bothTimestamp.value = parseInt(props.received as string) == 3;

  checkPostback();
  if (currentStates.value.indexOf(currentState.value) == -1) {
    currentState.value = 'account.scans.search.list';
  }
  if (scan.value.scanResponse === null) {
    scan.value.scanResponse = '';
  }
  if (scan.value.scanResponse.substr(0, 6) == "<curl>" && scan.value.scanResponse.substr(-7) == "</curl>") {
    scan.value.scanResponse = scan.value.scanResponse.substr(6, scan.value.scanResponse.length - 7 - 6);
    scan.value.resultType = 'curl';
  } else if ((scan.value.scanResponse.substr(0, 6) == "<html>" && scan.value.scanResponse.substr(-7) == "</html>") ||
      (scan.value.scanResponse.indexOf("<html>") != -1  && scan.value.scanResponse.indexOf("</html>") != -1)) {
    scan.value.scanResponse = encodeURIComponent(scan.value.scanResponse);
    scan.value.resultType = 'html';
  }
  detectEmergencyScan();

  isLoadingResults.value = false;
});

async function deleteScan () {
  if (isDisabled.value) {
    return;
  }
  isDisabled.value = true;
  let response = await crApi.requestV2('scan/delete/',{
    scanId: deletingScanId.value,
    website_token: securityService.getToken()
  });
  props.scanFilterStateV2.deletingScan = true;
  await props.scanFilterStateV2.loadScans();
  if (response.status == 1) {
    await flashMessenger.toastById('SAN_DELETED', 'success');
  } else {
    await flashMessenger.toastById(response.error, 'danger');
  }
  isDeleteScanModalOpen.value = false;
  deletingScanId.value = null;
  isDisabled.value = false;
}

async function checkPostback() {
  let pbProperty = await crApi.requestV2('scan_property/retrieve_scan_property/', {
    website_token: securityService.getToken(),
    scanId: scan.value.scanId,
    propertyName: 'successful_postback_url',
    response: 'extra'
  });
  if (pbProperty.status == 1) {
    isPostbackDone.value = true;
  } else {
    let failedPostbackAttempt = await crApi.requestV2('scan_property/retrieve_scan_property/', {
      website_token: securityService.getToken(),
      scanId: scan.value.scanId,
      propertyName: 'failed_postback_attempts',
      response: 'extra'
    });
    if (failedPostbackAttempt.status == 1) {
      isPostbackDone.value = true;
    }
  }
}

function detectEmergencyScan() {
  if (props.scan && props.scan.properties) {
    isEmergencyScan.value = props.scan.properties.some(prop => prop.emergency_mode === 1);
  }
}

const processAnswerText = (text: any) => {
  if (text.startsWith('geo://')) {
    const t = text.substr(6).split(',');
    return { type: 'geo', answerText: `${t[0]},${t[1]}`, link: '', thumbimg: '', file: '' };
  } else if (text.startsWith('<dropbox')) {
    const textCleaned = text.replace(/\\/g, ' ');
    const existLink = /link="([^"]+)"/.exec(textCleaned);
    const existFile = /file="([^"]+)"/.exec(textCleaned);
    return { type: 'dropbox', link: existLink ? existLink[1] : '', thumbimg: existLink ? existLink[1] : '', file: existFile ? existFile[1] : '' };
  } else if (text.startsWith('data:image/png;base64')) {
    const cleanedText = text.replace(/\\/g, ' ');
    return { type: 'base64', answerText: cleanedText, thumbimg: cleanedText, file: '' };
  } else if (text.startsWith('{')) {
    try {
      const answers = JSON.parse(text);
      return { type: 'json', link: answers.link, thumbimg: answers.link, file: answers.filename };
    } catch (e) {
      console.error('Error parsing JSON answer', e);
      return { type: '', answerText: text, link: '', thumbimg: '', file: '' };
    }
  } else {
    return { type: '', answerText: text, link: '', thumbimg: '', file: '' };
  }
};

const loadQuestions = async (scanId: any) => {
  if (!isLoadedQuestions.value) {
    isLoadingQuestions.value = true;
    const scanResponse = await crApi.requestV2('scan/retrieve_question/', {
      scanId: scanId,
      website_token: securityService.getToken()
    });
    if (scanResponse.status === 1) {
      isLoadedQuestions.value = true;
      scanQuestions.value = scanResponse.question.map(question => ({
        ...question,
        answer: question.answer.map(answer => {
          const processedAnswer = processAnswerText(answer.answerText);
          return {
            ...answer,
            ...processedAnswer
          };
        })
      }));
    }
    isLoadingQuestions.value = false;
  }
};

const loadProperties = async (scanId: any) => {
  if (!isLoadedProperties.value) {
    isLoadingProperties.value = true;
    try {
      const scanResponse = await crApi.requestV2('scan/retrieve_property/', {
        scanId: scanId,
        website_token: securityService.getToken()
      });
      if (scanResponse.status === 1) {
        isLoadedProperties.value = true;
        scanProperties.value = scanResponse.property.map(property => {
          let modifiedValue = '';
          let type = '';
          if (property.value && property.value.startsWith('geo://')) {
            const [lat, lng] = property.value.substr(6).split(',');
            modifiedValue = `${lat},${lng}`;
            type = 'geo';
          }
          return { ...property, type, modifiedValue };
        });
      }
    } catch (error) {
      console.error('Failed to load properties:', error);
    } finally {
      isLoadingProperties.value = false;
    }
  }
};

function imageError (answer: any) {
  answer.thumbimg = asset('icon/image_cancel_32.png');
}

function getDate(date, format) {
  if (!date) {
    return null;
  }
  let dt = date.replace('T',' ');
  let res = dt.slice(0, 19);
  return moment(res).format(format);
}

function decodeURI(uri) {
  return decodeURIComponent(uri);
}
</script>

<style scoped lang="scss">
:deep(.v-expansion-panel-title__overlay) {
  background-color: white !important;
  border: none !important;
}

:deep(.v-expansion-panels--variant-accordion > .v-expansion-panel::after) {
  display: none;
}

:deep(.v-expansion-panel-text__wrapper) {
  margin-top: -17px!important;
}

.hover-styling:hover {
  text-decoration: underline;
  cursor: pointer;
}
</style>